/**
 * Admin — Contact form settings.
 *
 * Lets the site admin change:
 *  - the recipient email address that receives contact-form submissions,
 *  - the subject line template,
 *  - the body template (with {name}, {email}, {subject}, {message}, {date}
 *    placeholders).
 *
 * Saved to Firestore at `siteConfig/contact`. Sending itself lives on the
 * public /contact page and is relayed by the Cloudflare Worker through Resend.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Mail, Save, Loader2, RotateCcw, Eye, CheckCircle2, AlertCircle } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { BackToDashboard } from "@/components/rawan/BackToDashboard";
import { useUserAuth } from "@/lib/user-auth";
import { useAuth } from "@/lib/auth";
import {
  DEFAULT_CONTACT_CONFIG,
  getContactConfig,
  renderTemplate,
  setContactConfig,
  type ContactConfig,
} from "@/lib/contact-config";

export const Route = createFileRoute("/admin/contact")({
  head: () => ({
    meta: [
      { title: "إعدادات نموذج الاتصال — روان UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminContactPage,
});

type Status =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "ok" }
  | { kind: "error"; message: string };

function AdminContactPage() {
  const { isAdmin: fbAdmin, loading: authLoading } = useUserAuth();
  const { isAuthed: localAdmin } = useAuth();
  const isAdmin = fbAdmin || localAdmin;
  const navigate = useNavigate();

  const [cfg, setCfg] = useState<ContactConfig>(DEFAULT_CONTACT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate({ to: "/" });
      return;
    }
    void (async () => {
      setLoading(true);
      const saved = await getContactConfig();
      setCfg(saved);
      setLoading(false);
    })();
  }, [authLoading, isAdmin, navigate]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setStatus({ kind: "saving" });
    try {
      await setContactConfig(cfg);
      setStatus({ kind: "ok" });
      setTimeout(() => setStatus({ kind: "idle" }), 4000);
    } catch (err) {
      setStatus({
        kind: "error",
        message:
          err instanceof Error ? err.message : "تعذّر الحفظ، تحقّق من صلاحيات Firestore.",
      });
    }
  }

  function resetDefaults() {
    setCfg({ ...DEFAULT_CONTACT_CONFIG });
  }

  if (!isAdmin) return null;

  const previewVars = {
    name: "جمال المثال",
    email: "jamal@example.com",
    subject: "استفسار عن المحتوى",
    message: "السلام عليكم،\nأودّ الاستفسار عن…",
    date: new Date().toLocaleString("ar"),
  };
  const previewSubject = renderTemplate(cfg.subjectTemplate, previewVars);
  const previewBody = renderTemplate(cfg.messageTemplate, previewVars);

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div
            className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl"
            style={{ background: "var(--gradient-primary)" }}
          >
            <Mail className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-extrabold">إعدادات نموذج الاتصال</h1>
            <p className="text-sm text-muted-foreground mt-1">
              تحكّم في عنوان المستلم وشكل الرسالة التي تصل من صفحة «اتصل بنا».
            </p>
          </div>
          <BackToDashboard />
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            جارٍ التحميل…
          </div>
        ) : (
          <form onSubmit={onSave} className="grid gap-5">
            <section className="bg-card border border-border rounded-2xl p-5 grid gap-3">
              <label className="text-sm font-bold">عنوان المستلم (Recipient email)</label>
              <input
                type="email"
                required
                value={cfg.recipient}
                onChange={(e) => setCfg((c) => ({ ...c, recipient: e.target.value }))}
                placeholder="jamal@elhizazi.me"
                className="px-4 py-3 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary transition"
              />
              <p className="text-xs text-muted-foreground leading-relaxed">
                هذا هو البريد الذي ستصل إليه الرسائل المرسلة من صفحة «اتصل بنا».
                <br />
                يمكنك تغيير هذا العنوان في أي وقت — سيقرأ نظام الإرسال العنوان المحفوظ
                هنا عند كل رسالة جديدة دون الحاجة لإعادة النشر.
              </p>
            </section>

            <section className="bg-card border border-border rounded-2xl p-5 grid gap-2">
              <div className="text-sm font-bold flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                طريقة الإرسال
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                يتم إرسال الرسائل عبر <b>Resend</b> فقط من عنوان الإرسال الموثّق
                في إعدادات الـ Worker. إذا وصلت الرسائل إلى البريد غير المرغوب،
                تأكّد من توثيق النطاق في Resend وإضافة سجلات <b>SPF</b> و <b>DKIM</b> و <b>DMARC</b>.
              </p>
            </section>

            <section className="bg-card border border-border rounded-2xl p-5 grid gap-3">
              <label className="text-sm font-bold">قالب عنوان الرسالة (Subject)</label>
              <input
                required
                value={cfg.subjectTemplate}
                onChange={(e) => setCfg((c) => ({ ...c, subjectTemplate: e.target.value }))}
                className="px-4 py-3 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary transition font-mono text-sm"
              />
            </section>

            <section className="bg-card border border-border rounded-2xl p-5 grid gap-3">
              <label className="text-sm font-bold">قالب نص الرسالة (Body)</label>
              <textarea
                required
                rows={12}
                value={cfg.messageTemplate}
                onChange={(e) => setCfg((c) => ({ ...c, messageTemplate: e.target.value }))}
                className="px-4 py-3 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary transition resize-y font-mono text-sm leading-relaxed"
              />
              <div className="text-xs text-muted-foreground leading-relaxed">
                المتغيرات المتاحة:
                {" "}
                <code className="px-1 rounded bg-muted">{"{name}"}</code>{" "}
                <code className="px-1 rounded bg-muted">{"{email}"}</code>{" "}
                <code className="px-1 rounded bg-muted">{"{subject}"}</code>{" "}
                <code className="px-1 rounded bg-muted">{"{message}"}</code>{" "}
                <code className="px-1 rounded bg-muted">{"{date}"}</code>
              </div>
            </section>

            <section className="bg-card border border-border rounded-2xl p-5 grid gap-3">
              <div className="flex items-center gap-2 text-sm font-bold">
                <Eye className="h-4 w-4 text-primary" />
                معاينة بمثال
              </div>
              <div className="text-xs text-muted-foreground">العنوان:</div>
              <div className="px-3 py-2 rounded-lg bg-background border border-border text-sm">
                {previewSubject}
              </div>
              <div className="text-xs text-muted-foreground">النص:</div>
              <pre className="px-3 py-3 rounded-lg bg-background border border-border text-xs whitespace-pre-wrap leading-relaxed font-mono">
                {previewBody}
              </pre>
            </section>

            {status.kind === "ok" ? (
              <div className="flex items-center gap-2 text-sm text-emerald-500 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                <CheckCircle2 className="h-4 w-4" />
                تم حفظ الإعدادات.
              </div>
            ) : null}
            {status.kind === "error" ? (
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2 text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <div>{status.message}</div>
                    {/Missing|insufficient|permission/i.test(status.message) ? (
                      <div className="text-foreground/80">
                        يجب نشر قواعد Firestore على Firebase أولاً. افتح{" "}
                        <a
                          href="https://console.firebase.google.com/project/rawanui/firestore/rules"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline font-medium"
                        >
                          Firebase Console → Firestore → Rules
                        </a>{" "}
                        ثم انسخ القواعد التالية والصقها ثم اضغط <b>Publish</b>:
                      </div>
                    ) : null}
                  </div>
                </div>
                {/Missing|insufficient|permission/i.test(status.message) ? (
                  <pre className="text-xs bg-card border border-border rounded-lg p-3 overflow-auto max-h-64 whitespace-pre-wrap" dir="ltr">{`match /siteConfig/{docId} {
  allow read: if true;
  allow write: if request.auth != null
    && request.auth.token.email in ["jamal@elhizazi.me", "rawan@elhizazi.me"];
}`}</pre>
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={status.kind === "saving"}
                className="btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {status.kind === "saving" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                حفظ الإعدادات
              </button>
              <button
                type="button"
                onClick={resetDefaults}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-card border border-border hover:bg-accent text-sm"
              >
                <RotateCcw className="h-4 w-4" />
                إعادة للوضع الافتراضي
              </button>
            </div>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}
